import PropTypes from 'prop-types';
import * as React from 'react';
import { useSnackbar } from 'notistack';
import { Box, Button } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { validationUtils } from '@jeremyling/react-material-ui-form-builder';
import { useForm } from 'react-hook-form';
import FullScreenDialog from 'components/modal/Modal';
// project import
import useStep from 'hooks/useStep';
import CommitButton from './buttons/CommitButton';
import withLoader from 'hoc/witLoader';
import ConfirmationModal from 'components/modal/ConfirmationModal';
import JobProgressLoader from 'components/job-progress -loader/JobProgressLoader';
import { ActionCloseReason } from 'constants/CloseReasonTypes';
import { JobProgressStatus } from 'constants/JobProgress';
import { TypeMode } from 'constants/TypeMode';
import MainCard from 'components/MainCard';
import { isEmpty } from 'lodash';

const StandardForm = ({
    title,
    formConfig,
    mode,
    defaultErrors,
    originalMessage,
    slice,
    defaultValue = {},
    onClose = () => {},
    onSubmit = (data, successCallback, errorCallback, requestCallback) => {},
    children,
}) => {
    const [open, setOpen] = React.useState(true);
    const [openModal, setOpenModal] = React.useState(false);
    const [requestId, setRequestId] = React.useState('');

    React.useEffect(() => {
        if (!requestId) return;
        setOpenModal(true);
    }, [requestId]);

    const [
        activeStep,
        {
            isLastStep,
            canGoToPrevStep,
            canGoToNextStep,
            goToNextStep,
            goToPrevStep,
        },
    ] = useStep({
        maxStep: formConfig.length - 1,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const schema = React.useMemo(
        () => validationUtils.getFormSchema(formConfig[activeStep].config()),
        [activeStep, formConfig],
    );

    const methods = useForm({
        mode: 'onSubmit',
        reValidateMode: 'onSubmit',
        resolver: yupResolver(schema),
        defaultValues: defaultValue,
    });

    //const w = useWatch({ control: methods.control });

    React.useEffect(() => {
        const values = methods.getValues();
        methods.reset({ ...defaultValue, ...values });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStep]);

    React.useEffect(() => {
        if (isEmpty(defaultErrors)) return;
        Object.keys(defaultErrors).forEach(field => {
            methods?.setError(field, {
                type: 'parser',
                message: defaultErrors[field],
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultErrors]);

    const { enqueueSnackbar } = useSnackbar();

    const handleClose = action => {
        setOpen(false);
        onClose(action);
    };

    const goTo = async move => {
        await methods.trigger();
        if (!methods.formState.isValid) {
            return;
        }
        if (move === 'next') {
            goToNextStep();
            return;
        }
        if (move === 'prev') {
            goToPrevStep();
            return;
        }
    };

    const resetToDefaults = () => {
        methods.reset(defaultValue);
    };

    const handleCommit = async (posts, actions) => {
        await methods.trigger();
        if (!isEmpty(methods.formState.errors)) {
            return;
        }
        actions.updateProps({ loading: true });
        posts = methods.getValues();
        onSubmit(
            posts,
            message => {
                enqueueSnackbar(message, { variant: 'success' });
                handleClose(ActionCloseReason.Submit);
                actions.updateProps({ loading: false });
            },
            (err, errMessage) => {
                const { errors } = err?.response?.data;
                if (errors?.message?.length > 0) {
                    errors.message.forEach(message => {
                        enqueueSnackbar(message, { variant: 'error' });
                    });
                } else {
                    enqueueSnackbar(err.message || errMessage, {
                        variant: 'error',
                    });
                }

                // else if (!errors || errors?.length === 0) {
                //     enqueueSnackbar(err.message || errMessage, {
                //         variant: "error",
                //     });
                // } else {
                //     errors.forEach((error) => {
                //         if (error?.type === "validation") {
                //             enqueueSnackbar(
                //                 `${CamelCaseToWords(error?.field)} - ${
                //                     error?.errorMessage
                //                 }`,
                //                 { variant: "error" }
                //             );
                //             if (!!error?.field) {
                //                 methods.setError(error?.field, {
                //                     type: "server",
                //                     message: error?.errorMessage,
                //                 });
                //             }
                //         }
                //     });
                // }
                actions.updateProps({ loading: false });
            },
        );
    };

    const onFinish = status => {
        try {
            if (status === JobProgressStatus?.Failed)
                throw new Error('Data not saved');
            mode === TypeMode.Create && resetToDefaults();
            enqueueSnackbar('Data saved', { variant: 'success' });
            handleCloseConfirmationModal();
            handleClose(ActionCloseReason.Submit);
        } catch (err) {
            enqueueSnackbar(err.message, { variant: 'error' });
        } finally {
        }
    };

    // const handleOpenConfirmationModal = (event) => {
    //     event.stopPropagation();
    //     setOpenModal(true);
    // };

    const handleCloseConfirmationModal = (event, reason) => {
        if (reason === 'backdropClick') return;
        setOpenModal(false);
        setRequestId('');
    };

    return (
        <FullScreenDialog
            open={open}
            title={title}
            onClose={() => handleClose(ActionCloseReason.Close)}
            actions={
                <>
                    {canGoToPrevStep && (
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => goTo('prev')}
                        >
                            Back
                        </Button>
                    )}
                    {canGoToNextStep && (
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => goTo('next')}
                        >
                            Continue
                        </Button>
                    )}
                    {isLastStep && (
                        <Box sx={{ marginRight: 3 }}>
                            {mode === TypeMode.Create ? (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() =>
                                        handleClose(ActionCloseReason.Close)
                                    }
                                    sx={{ marginRight: 3 }}
                                >
                                    Cancel
                                </Button>
                            ) : (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={resetToDefaults}
                                    sx={{ marginRight: 3 }}
                                >
                                    Reset
                                </Button>
                            )}
                            <CommitButton data={null} onCommit={handleCommit} />
                        </Box>
                    )}
                </>
            }
        >
            <Box>
                <form>
                    {children({
                        formConfig,
                        methods,
                        mode,
                        defaultValue: defaultValue,
                        activeStep,
                    })}
                </form>
            </Box>
            <ConfirmationModal
                onClose={handleCloseConfirmationModal}
                isOpen={openModal}
                title="Progress"
                aria-hidden={false}
                renderActions={() => (
                    <>
                        <Button
                            onClick={handleCloseConfirmationModal}
                            variant="outlined"
                            color="secondary"
                        >
                            Close
                        </Button>
                    </>
                )}
            >
                {requestId && (
                    <JobProgressLoader
                        requestId={requestId}
                        onFinish={onFinish}
                        isLinear={true}
                    />
                )}
            </ConfirmationModal>
        </FullScreenDialog>
    );
};

StandardForm.propTypes = {
    title: PropTypes.string.isRequired,
    formConfig: PropTypes.arrayOf(
        PropTypes.shape({
            // list: PropTypes.array,
            config: PropTypes.func.isRequired,
        }),
    ).isRequired,
    defaultValue: PropTypes.object,
    mode: PropTypes.oneOf(['create', 'edit']),
    onSubmit: PropTypes.func,
    onClose: PropTypes.func,
    children: PropTypes.func,
};

export default withLoader(StandardForm);
